import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import type { AutocompleteGetTagProps } from '@material-ui/lab/Autocomplete';
import { ReactNode } from 'react';
import { TEXT, createAdminPageStyles } from '../../style';

interface SearchAutocompleteProps<T> {
  id: string;
  options: T[];
  value: T | T[] | null;
  multiple?: boolean;
  disabled?: boolean;
  placeholder: string;
  getOptionLabel: (option: T) => string;
  getOptionSelected?: (option: T, value: T) => boolean;
  getOptionDisabled?: (option: T) => boolean;
  groupBy?: (option: T) => string;
  noOptionsText?: string;
  openOnFocus?: boolean;
  autoHighlight?: boolean;
  onChange: (value: T | T[] | null) => void;
  renderOption?: (option: T) => ReactNode;
  renderTags?: (value: T[], getTagProps: AutocompleteGetTagProps) => ReactNode;
}

const useStyles = createAdminPageStyles({
  autocomplete: {
    width: '100%',
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      paddingTop: 4,
      paddingBottom: 4,
    },
    '& .MuiAutocomplete-option': {
      fontFamily: 'Poppins',
      fontSize: 15,
    },
    '& .MuiAutocomplete-groupLabel': {
      fontFamily: 'Poppins',
      fontSize: 13,
      fontWeight: 700,
      color: TEXT,
      background: '#F7F8FB',
      lineHeight: '32px',
      top: 0,
    },
    '& .MuiAutocomplete-groupUl': {
      padding: 0,
    },
  },
});

function SearchAutocomplete<T extends { id: number }>({
  id,
  options,
  value,
  multiple = false,
  disabled = false,
  placeholder,
  getOptionLabel,
  getOptionSelected,
  getOptionDisabled,
  groupBy,
  noOptionsText = 'Aucun résultat',
  openOnFocus = true,
  autoHighlight = true,
  onChange,
  renderOption,
  renderTags,
}: SearchAutocompleteProps<T>) {
  const classes = useStyles();

  return (
    <Autocomplete
      id={id}
      className={classes.autocomplete}
      options={options}
      value={value}
      multiple={multiple}
      disabled={disabled}
      openOnFocus={openOnFocus}
      autoHighlight={autoHighlight}
      disableCloseOnSelect={multiple}
      filterSelectedOptions={multiple}
      getOptionLabel={getOptionLabel}
      getOptionSelected={
        getOptionSelected || ((option, selected) => Number(option.id) === Number(selected.id))
      }
      getOptionDisabled={getOptionDisabled}
      groupBy={groupBy}
      noOptionsText={noOptionsText}
      onChange={(_event, selected) => onChange(selected)}
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          className={classes.textField}
          placeholder={placeholder}
        />
      )}
    />
  );
}

export default SearchAutocomplete;
